import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useFragment } from "react-relay";

import { currentProfileAtom } from "@/features/Club/atoms";
import { InlineRestrictionsList } from "@/features/InlineRestrictions";
import {
  isBookingMultipleTeeTimesAtom,
  selectedTeeTimesAtom,
} from "@/features/TeeSheet/atoms";
import {
  isAccessibleBookingSlotAvailability,
  isBlockedOrUnavailableSlotAvailability,
  isOpenSlotAvailability,
} from "@/utils/slotAvailability";
import {
  BorderRadiusOption,
  PrivateTimeSlotCard,
} from "@whoosh/design-system/src/apps/PatronApp/screens/teesheet/components/TeeSheet/TimeSlotCard";
import { GetCourseLayoutIconFromCode } from "@whoosh/design-system/src/components/icons_v2/courseLayout";
import { GetTransportIconFromCode } from "@whoosh/design-system/src/components/icons_v2/transportation";
import { HStack } from "@whoosh/design-system/src/components/primitive";

import { PrivateTimeSlotCardWrapper_timeSlot$key } from "./__generated__/PrivateTimeSlotCardWrapper_timeSlot.graphql";

type Props = {
  timeSlotRef: PrivateTimeSlotCardWrapper_timeSlot$key;
  time?: string;
  availableSpots: number;
  canBookOnBusySlot: boolean | null | undefined;
  onPress?: () => void;
  borderRadius?: {
    top?: BorderRadiusOption;
    bottom?: BorderRadiusOption;
  };
  players: {
    id: string;
    firstName: string;
    lastName: string;
    type: "Member" | "Guest";
  }[];
  locationString?: string;
};

export const PrivateTimeSlotCardWrapper = ({
  borderRadius,
  availableSpots,
  canBookOnBusySlot,
  onPress,
  timeSlotRef,
  time,
  players,
  locationString,
}: Props) => {
  const currentProfile = useAtomValue(currentProfileAtom);
  const isBookingMultipleTeeTimes = useAtomValue(isBookingMultipleTeeTimesAtom);
  const selectedTeeTimes = useAtomValue(selectedTeeTimesAtom);

  const timeSlot = useFragment(
    PrivateTimeSlotCardWrapperFragmentString,
    timeSlotRef
  );

  const bookings =
    timeSlot?.bookings?.edges
      ?.map((e) => e?.node)
      .filter((b) => b?.status !== "DRAFT") ?? [];
  const viewerMemberId = currentProfile?.memberId;
  const rounds = bookings?.flatMap((b) =>
    b?.rounds?.edges?.map((e) => e?.node)
  );

  const timeSlotPlayers =
    rounds?.map((round) => {
      const player = round?.player;
      const isGuest = round?.__typename === "GuestRound";
      const fullName = player?.id
        ? `${player?.firstName} ${player?.lastName}${
            currentProfile?.memberId === player.id ? " " + t`(You)` : ""
          }`
        : isGuest
        ? t`Guest`
        : t`Member Hold`;
      const transportationIcon = GetTransportIconFromCode(
        round?.transportationType?.customCode ||
          round?.transportationType?.code ||
          "",
        "mainTiny"
      );
      const courseLayoutIcon = GetCourseLayoutIconFromCode(
        round?.courseLayout?.code || ""
      );
      return {
        fullName,
        isGuest,
        icons: (
          <HStack
            bg="grayscale-200"
            style={{ borderRadius: 4 }}
            height={20}
            alignItems="center"
            px={1}
            gap={".5"}
          >
            {transportationIcon
              ? transportationIcon({ width: 8, height: 8 })
              : null}
            {courseLayoutIcon
              ? courseLayoutIcon({ width: 8, height: 8 })
              : null}
          </HStack>
        ),
      };
    }) || [];

  const canUpdate = bookings.some((b) => b?.canUpdate) || false;
  const canJoin = bookings.map((b) => b?.writeAccess).includes("JOIN");
  const isFree = isOpenSlotAvailability(timeSlot.availability);
  const groupCanUpdate =
    isAccessibleBookingSlotAvailability(timeSlot.availability) &&
    availableSpots > 0 &&
    !!canBookOnBusySlot;

  const isUnavailable =
    isBlockedOrUnavailableSlotAvailability(timeSlot.availability) ||
    (isBookingMultipleTeeTimes && players.length !== 0);

  const isRowSelected = selectedTeeTimes?.includes(timeSlot.id);

  const shouldShowCheckbox = isBookingMultipleTeeTimes && players.length === 0;

  const isCurrentUserInBooking = rounds?.some(
    (e) => e?.player?.id === viewerMemberId
  );

  // A viewer counts as "an owner" of the booking if either:
  //   1. They are the authoritative `ownerClient` of the booking (covers the
  //      case where a host reserves multiple tee times to seat a larger group
  //      and has no round of their own on the subsequent bookings), or
  //   2. They are a player on the booking and the booking was surfaced with
  //      `writeAccess === "FULL_WRITE"` (covers the club's "All Members as
  //      Owners" setting, which grants every member-player owner-level
  //      permissions without setting `ownerClient`).
  // The `ownerClient` union includes a `%other` catch-all that has no `id`,
  // so guard with `"id" in ownerClient` before reading it.
  const isCurrentUserAnOwnerOfBooking = bookings.some((b) => {
    if (!viewerMemberId || !b) return false;

    const ownerClient = b.ownerClient;
    if (
      ownerClient &&
      "id" in ownerClient &&
      ownerClient.id === viewerMemberId
    ) {
      return true;
    }

    if (b.writeAccess !== "FULL_WRITE") return false;
    return (
      b.rounds?.edges?.some((e) => e?.node?.player?.id === viewerMemberId) ??
      false
    );
  });

  return (
    <PrivateTimeSlotCard
      restrictionList={<InlineRestrictionsList timeSlotRef={timeSlot} />}
      players={timeSlotPlayers}
      time={time}
      availableSpots={availableSpots}
      onPress={onPress}
      borderRadius={borderRadius}
      canUpdate={canUpdate || canJoin || isFree || groupCanUpdate}
      isUnavailable={isUnavailable}
      location={locationString}
      checkboxIsChecked={isRowSelected}
      shouldShowCheckbox={shouldShowCheckbox}
      currentUserIsInBooking={
        isCurrentUserInBooking || isCurrentUserAnOwnerOfBooking
      }
      ctaText={
        canUpdate && (isCurrentUserInBooking || isCurrentUserAnOwnerOfBooking)
          ? t`Your Booking`
          : undefined
      }
    />
  );
};

const PrivateTimeSlotCardWrapperFragmentString = graphql`
  fragment PrivateTimeSlotCardWrapper_timeSlot on TimeSlot {
    ...InlineRestrictionsList_timeSlot
    id
    availability
    bookings {
      edges {
        node {
          canUpdate
          writeAccess
          ownerClient {
            __typename
            ... on Member {
              id
            }
            ... on Customer {
              id
            }
          }
          ... on StandardBooking {
            status
          }
          ... on DiningBooking {
            status
          }
          ... on LessonBooking {
            status
          }
          rounds(noShowStatus: [PRESENT]) {
            edges {
              node {
                __typename
                courseLayout {
                  code
                }
                transportationType {
                  code
                  customCode
                  name
                }
                player {
                  id
                  firstName
                  lastName
                  __typename
                }
              }
            }
          }
        }
      }
    }
  }
`;
